import { classSelection } from './class-selection';
import { missionSelection } from './mission-selection';
import { useSyncExternalStore } from 'react';

/**
 * **« Je lis le travail de Léa » — la seule chose que cet onglet retient de ce geste.**
 *
 * ## Le manque, et pourquoi il ne se comblait pas côté serveur seul
 *
 * L'écran « espace d'un élève » donnait à l'enseignant l'identité, le dock reconstitué et un compte
 * par famille de fiches. Jamais une ligne. Pour lui montrer la GRILLE de son élève, il faut monter
 * l'outil de l'élève sur l'espace de l'élève — et le pipeline RPC des apps
 * (`apps/api/src/app-pipeline.ts`) résout l'espace depuis la SESSION de celui qui appelle, ce qui
 * est exactement la bonne défense : c'est elle qui empêche un élève de lire le fichier d'un
 * camarade en changeant un paramètre.
 *
 * Le serveur accepte donc désormais une DEMANDE, portée par deux en-têtes
 * (`x-ndrc-observe-class`, `x-ndrc-observe-student`), qu'il refuse si l'acteur n'est pas
 * l'enseignant de cette classe ou si l'élève n'en fait pas partie. Reste à les poser.
 *
 * ## Pourquoi les en-têtes sont posés ICI, et pas dans l'app
 *
 * Chaque app a son propre transport (`ndrc-apps/<id>/student/rpc.ts`, un `fetch` avec une adresse
 * en dur) : c'est son affaire, et le critère de réussite du projet (§52) est qu'on puisse ajouter
 * une app **sans modifier le cœur** — la réciproque vaut tout autant, le cœur ne doit pas avoir à
 * modifier les apps pour livrer une capacité qui ne les concerne pas. Une app n'a rien à savoir de
 * « qui regarde » : elle demande sa grille, elle reçoit une grille.
 *
 * Le bureau est l'HÔTE de ces apps : il les monte dans son propre document, dans son propre
 * onglet. Décorer les requêtes qu'elles émettent vers `/api/rpc/apps/` fait partie de ce rôle,
 * exactement comme monter la fenêtre autour d'elles. Ce module ne choisit jamais une adresse,
 * n'émet jamais une requête de lui-même, ne lit jamais une réponse : il AJOUTE deux en-têtes à des
 * requêtes que l'app a déjà décidé d'envoyer. `src/lib/rpc.ts` reste la seule porte réseau du
 * bureau (ses appels à `/api/rpc/core/` ne sont pas décorés : les opérations du cœur qui savent
 * lire l'espace d'un élève portent, elles, un `userId` explicite et vérifié — voir
 * `host/reviewWork.ts`).
 *
 * ## Pourquoi une variable de module, et pourquoi c'est sûr
 *
 * L'observation vaut pour TOUT le bureau de cet onglet : pendant qu'il lit le travail de Léa,
 * l'enseignant n'a aucune fenêtre ouverte sur son propre espace — `Desk` sert le même
 * `workspace` observé à toutes ses fenêtres, et `AppHost` les remonte toutes (`key` sur
 * l'identifiant d'espace). Un seul état suffit donc, et deux fenêtres ne peuvent pas se
 * contredire.
 *
 * Elle n'est écrite que par `startObservation`/`stopObservation`, appelées depuis un GESTE
 * (un clic), jamais pendant un rendu : l'état React et cette variable changent dans le même tour,
 * donc aucune requête ne peut partir entre les deux avec le mauvais en-tête.
 *
 * Et elle ne survit ni au rechargement, ni à la fermeture de l'onglet, ni à un autre onglet : rien
 * n'est écrit dans `localStorage` ni côté serveur. C'est délibéré. Un mode d'observation qui
 * persisterait ferait, un jour, projeter le travail privé d'un élève sur le vidéoprojecteur d'une
 * salle de classe parce qu'un second onglet aurait suivi le premier. Recharger ramène l'enseignant
 * chez lui : le défaut de sécurité par défaut est le bon.
 */

export interface ObservationTarget {
  classId: string;
  userId: string;
  /** Le nom de l'élève, tel que le bandeau l'affiche. Trente écrans se ressemblent ; se tromper
   * d'élève en plein débrief coûterait cher, et un identifiant ne dit rien à personne. */
  displayName: string;
}

const OBSERVE_CLASS_HEADER = 'x-ndrc-observe-class';
const OBSERVE_STUDENT_HEADER = 'x-ndrc-observe-student';

/** Les requêtes décorées, et elles seules : le RPC des apps. Ni `/api/rpc/core/`, ni les fichiers,
 * ni le fil temps réel, ni l'authentification. */
const APP_RPC_PREFIX = '/api/rpc/apps/';

let target: ObservationTarget | null = null;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function observationTarget(): ObservationTarget | null {
  return target;
}

/** Entrer en lecture. Appelée depuis un clic de l'enseignant (`teacher/StudentSpacePanel.tsx`). */
export function startObservation(next: ObservationTarget): void {
  target = next;
  announce();
}

/** Revenir chez soi. Le bandeau porte ce geste, et il est visible en permanence — plein écran
 * compris (`host/AppHost.tsx`). */
export function stopObservation(): void {
  if (target === null) return;
  target = null;
  announce();
}

/** De quel élève cet onglet lit le travail, ou `null`. `useSyncExternalStore` plutôt qu'un
 * contexte : l'état vit hors de React (le décorateur de requêtes ci-dessous le lit, et il n'est
 * pas un composant), et React doit lire la MÊME variable, jamais une copie qui pourrait retarder
 * d'un rendu sur les en-têtes réellement envoyés. */
export function useObservation(): ObservationTarget | null {
  return useSyncExternalStore(subscribe, observationTarget, () => null);
}

function pathOf(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.pathname;
  return new URL(input.url, window.location.origin).pathname;
}

let installed = false;

/**
 * Décore les requêtes que les apps hébergées envoient au RPC des apps, tant qu'une observation est
 * en cours. Installée une seule fois, au démarrage (`src/main.tsx`), avant que quoi que ce soit ne
 * soit monté.
 *
 * Hors observation, la fonction d'origine est appelée telle quelle, avec les MÊMES arguments : le
 * chemin de l'élève — la quasi-totalité des requêtes de cette plateforme — ne traverse aucun code
 * supplémentaire, et ne peut donc pas être cassé par celui-ci.
 *
 * Un `Request` déjà construit est reconstruit par `new Request(input, { headers })` plutôt que par
 * un étalement d'`init` : étaler `init` perdrait la méthode, le corps et les identifiants d'un
 * appelant qui n'aurait passé qu'un `Request`. Aucun appelant du dépôt ne le fait aujourd'hui —
 * raison de plus pour ne pas laisser un piège à celui de demain.
 */
export function installObservedAppFetch(): void {
  if (installed) return;
  installed = true;
  const original = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const observing = target;
    const mission = missionSelection();
    const selectedClass = classSelection();
    if (observing === null && mission === null && selectedClass === null)
      return original(input, init);
    if (!pathOf(input).startsWith(APP_RPC_PREFIX)) return original(input, init);

    const headers = new Headers(
      init?.headers ?? (input instanceof Request ? input.headers : undefined),
    );
    if (selectedClass) headers.set('x-ndrc-class', selectedClass);
    if (observing) {
      headers.set(OBSERVE_CLASS_HEADER, observing.classId);
      headers.set(OBSERVE_STUDENT_HEADER, observing.userId);
      headers.delete('x-ndrc-mission-run');
    } else if (mission) {
      headers.set('x-ndrc-mission-run', mission.runId);
    }
    if (input instanceof Request && init === undefined) {
      return original(new Request(input, { headers }));
    }
    return original(input, { ...init, headers });
  };
}
